import React, { useState, useRef } from "react";
import * as XLSX from "xlsx";
import { authHeader } from "../helpers";

const PRODUCT_DEFAULTS = {
  GenuineMessage: "Congratulations! You have bought Genuine Product",
  AlreadyScannedMsg: "This Product is Already Scanned",
  GenuineAudio:
    "http://market.toyosprings.com/api/files/638205429149547462.mp3",
  AlreadyScannedAudio:
    "http://market.toyosprings.com/api/files/638205429199798073.mp3",
  ProductImage:
    "http://market.toyosprings.com/api/files/638215660277041767.png",
  ProductConfiguration: JSON.stringify({
    CompanyLogo: true,
    ProductImage: true,
    ProductInformation: true,
    ProductRating: true,
    Share: true,
    Feedback: true,
    GenuineAudio: true,
    AlreadyScannedAudio: false,
    GenuineMessage: true,
    AlreadyScannedMessage: false,
    PromotionalMessages: true,
  }),
  ProductSpecification: JSON.stringify([{ key: "", value: "" }]),
};

const BADGE = {
  pending: { bg: "#eff6ff", color: "#3b82f6", label: "Pending" },
  success: { bg: "#f0fdf4", color: "#16a34a", label: "Done ✓" },
  partial: { bg: "#fffbeb", color: "#d97706", label: "Partial" },
  duplicate: { bg: "#f0f9ff", color: "#0284c7", label: "Exists" },
  error: { bg: "#fef2f2", color: "#dc2626", label: "Failed" },
};

const postJson = (url, body) =>
  fetch(url, {
    method: "POST",
    headers: { ...authHeader(), "Content-Type": "application/json" },
    body: JSON.stringify(body),
  }).then((r) => r.json());

export default function ProductImportExcel({ show, apiBaseUrl, onDone }) {
  const fileRef = useRef();

  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [summary, setSummary] = useState(null);
  const [step, setStep] = useState("pick");

  if (!show) return null;

  /* ── 1. Parse Excel ──────────────────────────────────────────── */
  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const wb = XLSX.read(evt.target.result, { type: "binary" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });

      const data = raw.filter(
        (r) =>
          r[1] &&
          String(r[1]).trim() !== "" &&
          String(r[1]).toUpperCase() !== "CODE" &&
          String(r[1]).toUpperCase() !== "NAN",
      );

      setRows(
        data.map((r, i) => ({
          _id: i,
          ProductCode: String(r[1]).trim(),
          ProductName: String(r[2]).trim(),
          LoyalityPoints: Number(r[3]) || 0,
          productStatus: "pending",
          loyaltyStatus: "pending",
          productDetail: "",
          loyaltyDetail: "",
          status: "pending",
        })),
      );
      setFileName(file.name);
      setSummary(null);
      setProgress(0);
      setStep("preview");
    };
    reader.readAsBinaryString(file);
  };

  const handleSubmit = async () => {
    if (!rows.length || importing) return;
    setImporting(true);
    setProgress(0);
    const updated = [...rows];
    let prodOk = 0,
      prodDup = 0,
      prodFail = 0;
    let loyOk = 0,
      loyFail = 0;

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const payload = {
        ProductId: 0,
        ProductCode: row.ProductCode,
        ProductName: row.ProductName,
        ProductDescription: row.ProductName,
        PermissibleScan: 0,
        IsActive: true,
        CategoryId: 0,
        ShareURL: "",
        ShareMessage: "",
        PromotionalMessage: "",
        PromotionalStartDate: null,
        PromotionalEndDate: null,
        VideoFile: null,
        IsUploadLocal: true,
        TotalRows: "0",
        CreatedDate: new Date().toLocaleDateString("en-US"),
        UpdatedDate: null,
        ...PRODUCT_DEFAULTS,
      };

      try {
        const data = await postJson(
          `${apiBaseUrl}ProductMaster/CreateProduct`,
          payload,
        );
        if (data.ResponseCode === "200") {
          updated[i] = {
            ...updated[i],
            productStatus: "success",
            productDetail: "Product inserted",
          };
          prodOk++;
        } else if (data.ResponseCode === "400") {
          updated[i] = {
            ...updated[i],
            productStatus: "duplicate",
            productDetail: data.ResponseMessage,
          };
          prodDup++;
        } else {
          updated[i] = {
            ...updated[i],
            productStatus: "error",
            productDetail: data.ResponseMessage || "Error",
          };
          prodFail++;
        }
      } catch (ex) {
        updated[i] = {
          ...updated[i],
          productStatus: "error",
          productDetail: ex.message,
        };
        prodFail++;
      }
      setRows([...updated]);
      setProgress(Math.round(((i + 1) / rows.length) * 99));
    }
    const loyaltyPayload = rows.map((r) => ({
      ProductCode: r.ProductCode,
      LoyalityPoints: r.LoyalityPoints,
    }));

    try {
      const loyData = await postJson(
        `${apiBaseUrl}LoyalityScheme/ImportFromExcel`,
        loyaltyPayload,
      );
      if (loyData.ResponseCode === "200") {
        const resultMap = {};
        (loyData.Data || []).forEach((d) => {
          resultMap[d.ProductCode] = d;
        });

        for (let i = 0; i < updated.length; i++) {
          const res = resultMap[updated[i].ProductCode];
          if (!res) {
            updated[i] = {
              ...updated[i],
              loyaltyStatus: "error",
              loyaltyDetail: "No response",
            };
            loyFail++;
          } else {
            const st = (res.Status || "").toLowerCase();
            if (st === "updated" || st === "inserted") {
              updated[i] = {
                ...updated[i],
                loyaltyStatus: "success",
                loyaltyDetail: `Points: ${updated[i].LoyalityPoints}`,
              };
              loyOk++;
            } else if (st === "notfound") {
              updated[i] = {
                ...updated[i],
                loyaltyStatus: "notfound",
                loyaltyDetail: "Code not found",
              };
              loyFail++;
            } else {
              updated[i] = {
                ...updated[i],
                loyaltyStatus: "error",
                loyaltyDetail: res.Message || "Error",
              };
              loyFail++;
            }
          }
        }
      } else {
        for (let i = 0; i < updated.length; i++) {
          updated[i] = {
            ...updated[i],
            loyaltyStatus: "error",
            loyaltyDetail: loyData.ResponseMessage || "Loyalty API error",
          };
          loyFail++;
        }
      }
    } catch (ex) {
      for (let i = 0; i < updated.length; i++) {
        updated[i] = {
          ...updated[i],
          loyaltyStatus: "error",
          loyaltyDetail: ex.message,
        };
        loyFail++;
      }
    }

    for (let i = 0; i < updated.length; i++) {
      const ps = updated[i].productStatus;
      const ls = updated[i].loyaltyStatus;
      if ((ps === "success" || ps === "duplicate") && ls === "success") {
        updated[i].status = "success";
      } else if (ps === "error" && ls === "error") {
        updated[i].status = "error";
      } else if (ps === "duplicate") {
        updated[i].status = "duplicate";
      } else {
        updated[i].status = "partial";
      }
    }

    setRows([...updated]);
    setProgress(100);
    setImporting(false);
    setSummary({
      total: rows.length,
      prodOk,
      prodDup,
      prodFail,
      loyOk,
      loyFail,
    });
    setStep("done");
  };

  /* ── Reset / Close ───────────────────────────────────────────── */
  const reset = () => {
    setRows([]);
    setFileName("");
    setSummary(null);
    setProgress(0);
    setStep("pick");
    setImporting(false);
    if (fileRef.current) fileRef.current.value = "";
  };
  const close = () => {
    reset();
    onDone();
  };

  /* ════════════════════════ RENDER ════════════════════════════════ */
  return (
    <>
      <div
        onClick={importing ? undefined : close}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.5)",
          zIndex: 1040,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: "#fff",
            borderRadius: 14,
            width: 860,
            maxWidth: "96vw",
            maxHeight: "92vh",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 24px 80px rgba(0,0,0,0.22)",
            overflow: "hidden",
          }}
        >
          {/* ══ HEADER ═══════════════════════════════════════════ */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "18px 24px",
              borderBottom: "1px solid #e5e7eb",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 9,
                  background: "#16a34a",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <svg width="25" height="25" fill="none" viewBox="0 0 24 24">
                  <path
                    d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                    stroke="#fff"
                    strokeWidth="2"
                    strokeLinejoin="round"
                  />
                  <polyline
                    points="14 2 14 8 20 8"
                    stroke="#fff"
                    strokeWidth="2"
                    strokeLinejoin="round"
                  />
                  <line
                    x1="8"
                    y1="13"
                    x2="16"
                    y2="13"
                    stroke="#fff"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <line
                    x1="8"
                    y1="17"
                    x2="16"
                    y2="17"
                    stroke="#fff"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
              <div>
                <div
                  style={{ fontWeight: 700, fontSize: 16, color: "#111827" }}
                >
                  Import Excel — Products with loyaltypoints
                </div>
                {/* <div style={{ fontSize: 12, color: "#6b7280", marginTop: 1 }}>
                  {step === "pick" &&
                    "One upload → inserts products AND updates loyalty points"}
                  {step === "preview" &&
                    `${rows.length} rows ready — review then submit`}
                  {step === "done" && "Import complete"}
                </div> */}
              </div>
            </div>
            {!importing && (
              <button
                onClick={close}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "#9ca3af",
                  fontSize: 22,
                  lineHeight: 1,
                  padding: 4,
                }}
              >
                ✕
              </button>
            )}
          </div>

          {/* ══ BODY ═════════════════════════════════════════════ */}
          <div style={{ flex: 1, overflowY: "auto", padding: "24px 24px 0" }}>
            {/* STEP: pick */}
            {step === "pick" && (
              <div>
                <label
                  htmlFor="xlsxInput"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 14,
                    border: "2px dashed #d1d5db",
                    borderRadius: 12,
                    padding: "52px 24px",
                    cursor: "pointer",
                    background: "#f9fafb",
                  }}
                >
                  <svg width="52" height="52" fill="none" viewBox="0 0 52 52">
                    <rect width="52" height="52" rx="12" fill="#dcfce7" />
                    <path
                      d="M26 17v14M20 24l6-7 6 7"
                      stroke="#16a34a"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M16 36h20"
                      stroke="#16a34a"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                    />
                  </svg>
                  <div style={{ textAlign: "center" }}>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: 15,
                        color: "#111827",
                      }}
                    >
                      Click to browse file
                    </div>
                    <div
                      style={{ color: "#6b7280", fontSize: 13, marginTop: 3 }}
                    >
                      .xlsx or .xls
                    </div>
                  </div>
                  <input
                    id="xlsxInput"
                    ref={fileRef}
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFile}
                    style={{ display: "none" }}
                  />
                </label>

                <div
                  style={{
                    marginTop: 14,
                    background: "#fffbeb",
                    border: "1px solid #fde68a",
                    borderRadius: 10,
                    padding: "14px 16px",
                    fontSize: 13,
                  }}
                >
                  <div
                    style={{
                      fontWeight: 700,
                      color: "#92400e",
                      marginBottom: 10,
                    }}
                  >
                    Expected Excel columns
                  </div>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ background: "#fef3c7" }}>
                        {[
                          "Sr. No.",
                          "CODE → ProductCode",
                          "DESCRIPTION → ProductName",
                          "POINTS → LoyaltyPts",
                        ].map((h) => (
                          <th
                            key={h}
                            style={{
                              padding: "6px 10px",
                              border: "1px solid #fde68a",
                              textAlign: "left",
                              color: "#78350f",
                              fontSize: 12,
                              fontWeight: h.includes("⭐") ? 800 : 600,
                            }}
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        [
                          "1",
                          "92 0101 00 A",
                          "TATA King Pin Kit 1312,1612,1613",
                          "25",
                        ],
                      ].map((r, i) => (
                        <tr
                          key={i}
                          style={{ background: i % 2 ? "#fffdf5" : "#fff" }}
                        >
                          {r.map((v, j) => (
                            <td
                              key={j}
                              style={{
                                padding: "6px 10px",
                                border: "1px solid #fde68a",
                                color: "#78350f",
                                fontSize: 12,
                                fontWeight: j === 3 ? 700 : 400,
                              }}
                            >
                              {v}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* STEP: preview / done */}
            {(step === "preview" || step === "done") && (
              <div>
                {/* File bar */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    background: "#f3f4f6",
                    borderRadius: 9,
                    padding: "10px 14px",
                    marginBottom: 14,
                  }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 10 }}
                  >
                    <span style={{ fontSize: 22 }}>📄</span>
                    <div>
                      <div
                        style={{
                          fontWeight: 600,
                          fontSize: 14,
                          color: "#111827",
                        }}
                      >
                        {fileName}
                      </div>
                      <div style={{ fontSize: 12, color: "#6b7280" }}>
                        {rows.length} rows detected
                      </div>
                    </div>
                  </div>
                  {step === "preview" && !importing && (
                    <button
                      onClick={reset}
                      style={{
                        background: "#fff",
                        border: "1px solid #d1d5db",
                        borderRadius: 6,
                        padding: "5px 14px",
                        cursor: "pointer",
                        fontSize: 12,
                        color: "#374151",
                      }}
                    >
                      Change file
                    </button>
                  )}
                </div>

                {/* Progress bar */}

                {importing && (
                  <div style={{ marginBottom: 14 }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: 12,
                        color: "#6b7280",
                        marginBottom: 5,
                      }}
                    >
                      <span>
                        Processing {progress} of {rows.length} rows…
                      </span>
                      <span>{progress}%</span>
                    </div>
                    <div
                      style={{
                        background: "#e5e7eb",
                        borderRadius: 99,
                        height: 9,
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          width: `${progress}%`,
                          height: "100%",
                          borderRadius: 99,
                          background: "#16a34a",
                          transition: "width .25s ease",
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Summary */}
                {summary && (
                  <div style={{ marginBottom: 14 }}>
                    <div
                      style={{
                        display: "flex",
                        gap: 10,
                        flexWrap: "wrap",
                        marginBottom: 8,
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 700,
                          fontSize: 13,
                          color: "#374151",
                          alignSelf: "center",
                        }}
                      >
                        Products:
                      </div>
                      {[
                        { label: `Inserted: ${summary.prodOk}`, bg: "#16a34a" },
                        { label: `Exists: ${summary.prodDup}`, bg: "#0284c7" },
                        { label: `Failed: ${summary.prodFail}`, bg: "#dc2626" },
                      ].map((s) => (
                        <div
                          key={s.label}
                          style={{
                            background: s.bg,
                            color: "#fff",
                            borderRadius: 7,
                            padding: "5px 14px",
                            fontWeight: 700,
                            fontSize: 13,
                          }}
                        >
                          {s.label}
                        </div>
                      ))}
                    </div>
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                      <div
                        style={{
                          fontWeight: 700,
                          fontSize: 13,
                          color: "#374151",
                          alignSelf: "center",
                        }}
                      >
                        Loyalty:
                      </div>
                      {[
                        { label: `Updated: ${summary.loyOk}`, bg: "#7c3aed" },
                        { label: `Failed: ${summary.loyFail}`, bg: "#dc2626" },
                      ].map((s) => (
                        <div
                          key={s.label}
                          style={{
                            background: s.bg,
                            color: "#fff",
                            borderRadius: 7,
                            padding: "5px 14px",
                            fontWeight: 700,
                            fontSize: 13,
                          }}
                        >
                          {s.label}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Table — shows both product + loyalty status per row */}
                <div
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 9,
                    overflow: "hidden",
                  }}
                >
                  {/* Header */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "36px 150px 1fr 55px 110px 110px",
                      background: "#f9fafb",
                      borderBottom: "2px solid #e5e7eb",
                      padding: "10px 14px",
                      fontSize: 11,
                      fontWeight: 700,
                      color: "#6b7280",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    <div>#</div>
                    <div>Code</div>
                    <div>Name</div>
                    <div>Pts</div>
                    <div>Product</div>
                    <div>Loyalty</div>
                  </div>

                  {/* Rows */}
                  <div style={{ maxHeight: 340, overflowY: "auto" }}>
                    {rows.map((row, i) => {
                      const pb = {
                        pending: {
                          bg: "#eff6ff",
                          color: "#3b82f6",
                          label: "Pending",
                        },
                        success: {
                          bg: "#f0fdf4",
                          color: "#16a34a",
                          label: "Inserted",
                        },
                        duplicate: {
                          bg: "#f0f9ff",
                          color: "#0284c7",
                          label: "Exists",
                        },
                        error: {
                          bg: "#fef2f2",
                          color: "#dc2626",
                          label: "Failed",
                        },
                      }[row.productStatus] || {
                        bg: "#f3f4f6",
                        color: "#6b7280",
                        label: "-",
                      };

                      const lb = {
                        pending: {
                          bg: "#eff6ff",
                          color: "#3b82f6",
                          label: "Pending",
                        },
                        success: {
                          bg: "#f5f3ff",
                          color: "#7c3aed",
                          label: "Updated",
                        },
                        notfound: {
                          bg: "#fdf4ff",
                          color: "#9333ea",
                          label: "Not Found",
                        },
                        error: {
                          bg: "#fef2f2",
                          color: "#dc2626",
                          label: "Failed",
                        },
                      }[row.loyaltyStatus] || {
                        bg: "#f3f4f6",
                        color: "#6b7280",
                        label: "-",
                      };

                      return (
                        <div
                          key={row._id}
                          style={{
                            display: "grid",
                            gridTemplateColumns:
                              "36px 150px 1fr 55px 110px 110px",
                            padding: "8px 14px",
                            alignItems: "center",
                            background: i % 2 === 0 ? "#fff" : "#fafafa",
                            borderBottom: "1px solid #f3f4f6",
                            fontSize: 12,
                          }}
                        >
                          <div style={{ color: "#9ca3af" }}>{i + 1}</div>
                          <div
                            style={{
                              fontFamily: "monospace",
                              color: "#374151",
                              fontWeight: 600,
                              fontSize: 11,
                            }}
                          >
                            {row.ProductCode}
                          </div>
                          <div style={{ color: "#111827", fontSize: 12 }}>
                            {row.ProductName}
                          </div>
                          <div style={{ fontWeight: 700, color: "#7c3aed" }}>
                            {row.LoyalityPoints}
                          </div>
                          {/* Product badge + tooltip */}
                          <div title={row.productDetail}>
                            <span
                              style={{
                                background: pb.bg,
                                color: pb.color,
                                borderRadius: 99,
                                padding: "2px 9px",
                                fontSize: 11,
                                fontWeight: 700,
                                cursor: "default",
                              }}
                            >
                              {pb.label}
                            </span>
                          </div>
                          {/* Loyalty badge + tooltip */}
                          <div title={row.loyaltyDetail}>
                            <span
                              style={{
                                background: lb.bg,
                                color: lb.color,
                                borderRadius: 99,
                                padding: "2px 9px",
                                fontSize: 11,
                                fontWeight: 700,
                                cursor: "default",
                              }}
                            >
                              {lb.label}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <div
            style={{
              padding: "16px 24px",
              borderTop: "1px solid #e5e7eb",
              display: "flex",
              justifyContent: "flex-end",
              gap: 10,
              background: "#f9fafb",
              marginTop: 20,
            }}
          >
            {step === "done" ? (
              <button
                onClick={close}
                style={{
                  background: "#16a34a",
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  padding: "11px 32px",
                  fontWeight: 700,
                  fontSize: 14,
                  cursor: "pointer",
                }}
              >
                ✓ Done
              </button>
            ) : (
              <>
                <button
                  onClick={close}
                  disabled={importing}
                  style={{
                    background: "#fff",
                    color: "#374151",
                    border: "1px solid #d1d5db",
                    borderRadius: 8,
                    padding: "11px 22px",
                    fontWeight: 600,
                    fontSize: 14,
                    cursor: importing ? "not-allowed" : "pointer",
                    opacity: importing ? 0.5 : 1,
                  }}
                >
                  Cancel
                </button>

                {step === "preview" && (
                  <button
                    onClick={handleSubmit}
                    disabled={importing || !rows.length}
                    style={{
                      background: importing ? "#86efac" : "#16a34a",
                      color: "#fff",
                      border: "none",
                      borderRadius: 8,
                      padding: "11px 28px",
                      fontWeight: 700,
                      fontSize: 14,
                      cursor: importing ? "not-allowed" : "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      minWidth: 220,
                      justifyContent: "center",
                    }}
                  >
                    {importing ? (
                      <>
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          style={{ animation: "xlsxSpin 1s linear infinite" }}
                        >
                          <circle
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="#fff"
                            strokeWidth="3"
                            strokeOpacity=".3"
                          />
                          <path
                            d="M12 2a10 10 0 0 1 10 10"
                            stroke="#fff"
                            strokeWidth="3"
                            strokeLinecap="round"
                          />
                        </svg>
                        Processing… {progress}%
                      </>
                    ) : (
                      `Submit ${rows.length} rows`
                    )}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <style>{`@keyframes xlsxSpin{to{transform:rotate(360deg);}}`}</style>
    </>
  );
}
